import styles from './WaitingList.module.css'
import { useAppDispatch, useAppSelector } from '../../redux/types/types'
import CustomButton from '../UI/CustomButton/CustomButton'
import CustomModal from '../UI/CustomModal/CustomModal'
import CreateClaim from '../CreateClaim/CreateClaim'
import { useEffect, useState, useMemo } from 'react'
import {
  changeClaimMainInformation,
  getAllClaimsFromPlatforms,
  getCurrentClaim,
} from '../../redux/actions/claimsAction'
import { RequestType } from '../../redux/actions/types'

import {
  createRuStatusValueForClaim,
  createStyleForStatusClaim,
} from '../../utils/createStatusForClaims'
import { useNavigate, useParams } from 'react-router-dom'
import PATH from '../../navigation/path'
import { ItemInfo } from '../ItemInfo/ItemInfo'
import { getClients } from '../../redux/actions/clientsAction'
import { parseDateSafely } from '../../utils/date'
import Loader from '../UI/Loader/Loader'

interface WaitingListProps {
  platformSize: number
  platformName: string
  startDate: any
  placeId: number
}

const WaitingList: React.FC<WaitingListProps> = ({
  startDate,
  platformSize,
  platformName,
  placeId,
}) => {
  const navigate = useNavigate()

  const { claimId } = useParams<{ claimId: string }>()
  const { currentClaim, allClaimsFromPlaces, isLoading, error } = useAppSelector(
    (state) => state.claims,
  )
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [errorClaimId, setErrorClaimId] = useState<number | null>(null)

  const [yearsAndMonth, setYearsAndMonth] = useState('2025-02')
  const [id, setId] = useState<number>(-1)
  const [refresh, setRefresh] = useState(false)
  const { clients } = useAppSelector((state) => state.clients)
  const dispatch = useAppDispatch()
  const { user, userRole, token } = useAppSelector((state) => state.auth)

  const [createClaimModal, setCreateClaimModal] = useState<boolean>(false)
  const [isLocalLoading, setIsLocalLoading] = useState<boolean>(true)
  const [cachedClaims, setCachedClaims] = useState<any[]>([])
  const date = parseDateSafely(startDate)

  useEffect(() => {
    if (currentClaim && currentClaim.startDate) {
      const startDate = parseDateSafely(currentClaim.startDate)
      const year = startDate.getFullYear()
      const month = startDate.getMonth() + 1
      setYearsAndMonth(`${year}-${month < 10 ? '0' + month : month}`)
    }
  }, [currentClaim])

  useEffect(() => {
    dispatch(getClients({ token }) as any)
  }, [dispatch, token])

  useEffect(() => {
    const payload: any = {
      yearsAndMonth,
      token,
    }

    if (user.role !== 'superAdmin' && user.employee?.company?.id) {
      payload.companyId = String(user.employee.company.id)
    }

    dispatch(getAllClaimsFromPlatforms(payload))
  }, [dispatch, yearsAndMonth, user.role, user.employee?.company?.id, token])

  useEffect(() => {
    if (claimId && token && (!currentClaim?.id || currentClaim.id !== Number(claimId))) {
      dispatch(getCurrentClaim({ claimId, token }))
    }
  }, [dispatch, claimId, token, currentClaim?.id])

  useEffect(() => {
    if (allClaimsFromPlaces && allClaimsFromPlaces.length > 0) {
      setCachedClaims(allClaimsFromPlaces)
    }
  }, [allClaimsFromPlaces])

  useEffect(() => {
    if (isLoading || !currentClaim?.id) {
      setIsLocalLoading(true)
    } else {
      const timer = setTimeout(() => {
        setIsLocalLoading(false)
      }, 500)

      return () => clearTimeout(timer)
    }
  }, [isLoading, currentClaim?.id])

  const filteredAndSortedClaims = useMemo(() => {
    if (
      !currentClaim?.id ||
      ((!allClaimsFromPlaces || allClaimsFromPlaces.length === 0) &&
        (!cachedClaims || cachedClaims.length === 0))
    ) {
      return []
    }

    const claimsToUse =
      allClaimsFromPlaces && allClaimsFromPlaces.length > 0 ? allClaimsFromPlaces : cachedClaims

    if (!claimsToUse || claimsToUse.length === 0) {
      return []
    }

    const currentPlaceClaims = claimsToUse.filter((item) => item.id === placeId)

    if (!currentPlaceClaims.length) {
      return []
    }

    const currentPlaceClaim = currentPlaceClaims[0]

    if (!currentClaim.startDate || !currentClaim.endDate) {
      return []
    }

    const waitingRequests = currentPlaceClaim.requests.filter(
      (request: RequestType) =>
        request.status === 'inAnticipation' &&
        request.startDate &&
        request.endDate &&
        new Date(request.startDate) <= new Date(currentClaim.endDate) &&
        new Date(request.endDate) >= new Date(currentClaim.startDate) &&
        request.id !== currentClaim.id,
    )

    return waitingRequests.sort((a: RequestType, b: RequestType) => {
      const dateA = new Date(a.createdAt).getTime()
      const dateB = new Date(b.createdAt).getTime()
      return dateA - dateB
    })
  }, [allClaimsFromPlaces, cachedClaims, currentClaim, placeId])
  const switchWithExisting = async () => {
    const newClaimRequest = filteredAndSortedClaims.find(
      (request: RequestType) => request.id === id,
    )

    if (!newClaimRequest) {
      console.error('New claim request not found')
      return
    }

    const newStart = parseDateSafely(newClaimRequest.startDate)
    const newEnd = parseDateSafely(newClaimRequest.endDate)

    const claimsToUse =
      allClaimsFromPlaces && allClaimsFromPlaces.length > 0 ? allClaimsFromPlaces : cachedClaims
    const overlappingRequests = claimsToUse
      .flatMap((claim) => claim.requests)
      .filter(
        (request: RequestType) =>
          request.status === 'inProgress' &&
          ((newStart >= parseDateSafely(request.startDate) &&
            newStart <= parseDateSafely(request.endDate)) ||
            (newEnd >= parseDateSafely(request.startDate) &&
              newEnd <= parseDateSafely(request.endDate)) ||
            (newStart <= parseDateSafely(request.startDate) &&
              newEnd >= parseDateSafely(request.endDate))),
      )

    for (const request of overlappingRequests) {
      await dispatch(
        changeClaimMainInformation({
          token,
          claimId: String(request.id),
          payload: { status: 'inAnticipation' },
        }),
      )
    }

    await dispatch(
      changeClaimMainInformation({
        token,
        claimId: String(id),
        payload: { status: 'inProgress' },
      }),
    )

    await dispatch(getCurrentClaim({ claimId, token }))
    await dispatch(
      getAllClaimsFromPlatforms({
        yearsAndMonth,
        token,
        ...(user.role !== 'superAdmin' &&
          user.employee?.company?.id && {
            companyId: String(user.employee.company.id),
          }),
      }),
    )

    navigate(`${PATH.claimCardPage}${id}`)
    setShowErrorModal(false)
    setRefresh((prev) => !prev)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const changeStatus = async (claimId: number) => {
    setId(claimId)

    const requestToUpdate = filteredAndSortedClaims.find(
      (request: RequestType) => request.id === claimId,
    )

    if (!requestToUpdate) {
      console.error('Request not found')
      return
    }

    const currentStart = parseDateSafely(requestToUpdate.startDate)
    const currentEnd = parseDateSafely(requestToUpdate.endDate)

    const claimsToUse =
      allClaimsFromPlaces && allClaimsFromPlaces.length > 0 ? allClaimsFromPlaces : cachedClaims
    const inProgressRequests = claimsToUse
      .flatMap((claim) => claim.requests)
      .filter((request: RequestType) => request.status === 'inProgress' && request.id !== claimId)

    let isOverlapping = false
    let conflictingClaimId: number | null = null

    inProgressRequests.forEach((request) => {
      const requestStart = parseDateSafely(request.startDate)
      const requestEnd = parseDateSafely(request.endDate)

      const isOverlap =
        (currentStart >= requestStart && currentStart <= requestEnd) ||
        (currentEnd >= requestStart && currentEnd <= requestEnd) ||
        (currentStart <= requestStart && currentEnd >= requestEnd)

      if (isOverlap) {
        isOverlapping = true
        conflictingClaimId = request.id
        return
      }
    })

    if (isOverlapping && conflictingClaimId) {
      setErrorMessage('Уже есть бронирование на этот период.')
      setErrorClaimId(conflictingClaimId)
      setShowErrorModal(true)
    } else {
      await dispatch(
        changeClaimMainInformation({
          token,
          claimId: String(claimId),
          payload: { status: 'inProgress' },
        }),
      )

      await dispatch(getCurrentClaim({ claimId: String(claimId), token }))
      await dispatch(
        getAllClaimsFromPlatforms({
          yearsAndMonth,
          token,
          ...(user.role !== 'superAdmin' &&
            user.employee?.company?.id && {
              companyId: String(user.employee.company.id),
            }),
        }),
      )

      setErrorMessage('Бронирование успешно создано.')
      setErrorClaimId(claimId)
      setShowErrorModal(true)
    }
  }

  const renderClaimInfo = (allClaims: any, data: any) => {
    const startDate = data.startDate.slice(0, 10).split('-').reverse().join('.')
    const startTime = data.startDate.slice(11, 16).split('-').reverse().join('.')
    const endDate = data.endDate.slice(0, 10).split('-').reverse().join('.')
    const endTime = data.endDate.slice(11, 16).split('-').reverse().join('.')

    return (
      <div className={styles.mainInformationBlock} key={data.id}>
        <div className={styles.mainInformationContainer}>
          <ItemInfo title='Зал' value={allClaims.name} />
          <ItemInfo title='Город' value={allClaims.city} />
          <ItemInfo title='Менеджер' value={allClaims.company.contactFullName} />
          <ItemInfo title='Дата и время начала' value={`${startDate}, ${startTime} `} />
          <ItemInfo title='Дата и время окончания' value={`${endDate}, ${endTime}`} />
          <ItemInfo title='Количество гостей' value={`${data.peopleCount} человек(a)`} />
          <ItemInfo
            title='Статус'
            value={createRuStatusValueForClaim(data.status)}
            classNameValue={createStyleForStatusClaim(data.status)}
          />
          {/* <div className={styles.end}>
            <CustomButton title='Бронировать' onClick={() => changeStatus(data.id)} />
          </div> */}
        </div>
      </div>
    )
  }

  if (isLocalLoading) {
    return (
      <div className={styles.waitList}>
        <div className={styles.titleBlock}>
          <h2 className={styles.title}>{'Лист ожидания'}</h2>
          <CustomButton
            className={styles.btnAdd}
            title='Добавить в лист ожидания'
            onClick={() => setCreateClaimModal(true)}
          />
        </div>
        <Loader />
      </div>
    )
  }

  if (!currentClaim || !currentClaim.id) {
    return (
      <div className={styles.waitList}>
        <div className={styles.titleBlock}>
          <h2 className={styles.title}>{'Лист ожидания'}</h2>
          <CustomButton
            className={styles.btnAdd}
            title='Добавить в лист ожидания'
            onClick={() => setCreateClaimModal(true)}
          />
        </div>
        <div className={styles.noData}>
          <p>Загрузка данных заявки...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.waitList}>
        <div className={styles.titleBlock}>
          <h2 className={styles.title}>{'Лист ожидания'}</h2>
          <CustomButton
            className={styles.btnAdd}
            title='Добавить в лист ожидания'
            onClick={() => setCreateClaimModal(true)}
          />
        </div>
        <div className={styles.noData}>
          <p>Ошибка загрузки данных: {String(error)}</p>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.waitList}>
      <CustomModal visible={showErrorModal} setVisible={setShowErrorModal}>
        <div className={styles.errorModalContent}>
          <h3
            style={{
              color: errorMessage === 'Уже есть бронирование на этот период.' ? 'red' : 'green',
            }}
          >
            {errorMessage === 'Уже есть бронирование на этот период.'
              ? 'Ошибка бронирования'
              : 'Бронирование подтверждено'}
          </h3>
          <p>{errorMessage}</p>
          {errorMessage === 'Уже есть бронирование на этот период.' ? (
            <>
              <CustomButton
                title='Перейти к бронированию'
                onClick={() => {
                  navigate(`${PATH.claimCardPage}${errorClaimId}`)
                  setShowErrorModal(false)
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                }}
              />
              <p style={{ marginTop: '20px' }}>Или отменить состоявшийся бронирование и</p>
              <CustomButton title='Бронировать новый' onClick={switchWithExisting} />
            </>
          ) : (
            <CustomButton
              title='Перейти к бронированию'
              onClick={() => {
                navigate(`${PATH.claimCardPage}${errorClaimId}`)
                setShowErrorModal(false)
                window.scrollTo({ top: 0, behavior: 'smooth' })
              }}
            />
          )}
        </div>
      </CustomModal>

      <div className={styles.titleBlock}>
        <h2 className={styles.title}>{'Лист ожидания'}</h2>
        <CustomButton
          className={styles.btnAdd}
          title='Добавить в лист ожидания'
          onClick={() => setCreateClaimModal(true)}
        />
      </div>

      {filteredAndSortedClaims.length > 0 ? (
        filteredAndSortedClaims.map((request: RequestType) => {
          const claimsToUse =
            allClaimsFromPlaces && allClaimsFromPlaces.length > 0
              ? allClaimsFromPlaces
              : cachedClaims
          const placeClaim = claimsToUse.find((claim) => claim.id === placeId)
          return placeClaim ? renderClaimInfo(placeClaim, request) : null
        })
      ) : (
        <div className={styles.noData}>
          <p>В листе ожидания нет заявок для данного периода</p>
        </div>
      )}

      <CustomModal visible={createClaimModal} setVisible={setCreateClaimModal}>
        <CreateClaim
          isWaitingList
          client={clients}
          id={placeId}
          size={platformSize}
          customer={user.fullName}
          platformName={platformName}
          startDate={date ? date : new Date()}
          onClickHandler={() => setCreateClaimModal(false)}
        />
      </CustomModal>
    </div>
  )
}

export default WaitingList
